/**
 * @typedef {Object} ConstructorProduct
 * @property {string} handle - Product handle/slug
 * @property {Object} constructorData - Raw Constructor.io data
 * @property {Array} variations - Product variations
 * @property {boolean} isSlotted - Whether product is slotted
 */

/**
 * Constructor.io Browse Integration
 * @class
 */
class ConstructorBrowse {
  constructor() {
    if (ConstructorBrowse.instance) {
      return ConstructorBrowse.instance;
    }

    this.client = null;
    this.isInitialized = false;
    this.requestSequence = 0;
    this.retryCount = 0;
    this.currentCollectionHandle = "";
    this.currentFilters = {};
    this.currentFacets = [];

    ConstructorBrowse.instance = this;
    this._setupBrowse();
  }

  /**
   * Get or create singleton instance
   */
  static getInstance() {
    if (!ConstructorBrowse.instance) {
      ConstructorBrowse.instance = new ConstructorBrowse();
    }
    return ConstructorBrowse.instance;
  }

  getImageUrl(constructorData) {
    return constructorData.image_url || constructorData.mediaImages?.[0]?.transformedSrc || "";
  }

  getSecondaryImageUrl(constructorData) {
    return constructorData.mediaImages?.[1]?.transformedSrc || null;
  }

  /**
   * Automatic setup when browse instance is created
   */
  async _setupBrowse() {
    try {
      this.client = await window.constructorManager.getClient();
      this.bindEvents();
      this.isInitialized = true;

      document.dispatchEvent(
        new CustomEvent("constructor:browse:ready", {
          detail: { instance: this },
        })
      );
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent("constructor:error", {
          detail: {
            message: error.message,
            stack: error.stack,
            context: "browse setup",
            timestamp: Date.now(),
            url: window.location.href,
          },
        })
      );
    }
  }

  /**
   * Fetch products from a collection
   * @param {string} collectionHandle - Collection handle
   * @param {Object} options - Search options
   * @returns {Promise<boolean>} - Success of operation
   */
  async handleBrowse(collectionHandle, options = {}) {
    if (!this.isInitialized || !this.client || !window.constructorio) {
      return false;
    }

    this.requestSequence++;
    const mySequence = this.requestSequence;

    this.currentCollectionHandle = collectionHandle;

    if (options.filters !== undefined) {
      this.currentFilters = options.filters;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = options.page || parseInt(urlParams.get("page")) || 1;
    const resultsPerPage = options.resultsPerPage || parseInt(urlParams.get("resultsPerPage")) || 40;

    let sortBy = options.sortBy || urlParams.get("sortBy");
    let sortOrder = options.sortOrder || urlParams.get("sortOrder");

    const legacySort = urlParams.get("sort");
    if (!sortBy && legacySort && legacySort.includes(":")) {
      const [sb, so] = legacySort.split(":");
      sortBy = sb || "relevance";
      sortOrder = so || "descending";
    }
    sortBy = sortBy || "relevance";
    sortOrder = sortOrder || "descending";

    if (options.filters === undefined) {
      const urlFilters = urlParams.get("filters");
      let parsedFilters = {};
      if (urlFilters) {
        try {
          parsedFilters = JSON.parse(decodeURIComponent(urlFilters));
          this.currentFilters = { ...parsedFilters, ...this.currentFilters };
        } catch (e) {}
      }
    }

    this.currentPage = currentPage;
    try {
      const cleanOptions = {};
      Object.keys(options).forEach((key) => {
        const value = options[key];
        if (value !== undefined && value !== null && value !== "") {
          cleanOptions[key] = value;
        }
      });

      const browseParams = {};

      if (resultsPerPage !== undefined && resultsPerPage !== null) {
        browseParams.resultsPerPage = resultsPerPage;
      }

      browseParams.section = "Products";

      if (this.currentFilters && Object.keys(this.currentFilters).length > 0) {
        browseParams.filters = this.currentFilters;
      }

      if (sortBy !== undefined && sortBy !== null && sortBy !== "") {
        browseParams.sortBy = sortBy;
      }

      if (sortOrder !== undefined && sortOrder !== null && sortOrder !== "") {
        browseParams.sortOrder = sortOrder;
      }

      if (currentPage !== undefined && currentPage !== null && currentPage > 0) {
        browseParams.page = currentPage;
      }

      browseParams.fmt_options = {
        show_hidden_facets: false,
        show_hidden_fields: false,
        fields: [
          "data.handle",
          "data.title",
          "data.price",
          "data.compareAtPrice",
          "data.image_url",
          "data.inventory_quantity",
          "data.group_ids",
          "data.preorder_tag",
          "data.waterproof_tag",
          "data.bestseller_tag",
          "data.new_tag",
          "data.mediaImages",
          "data.url",
          "data.tags",
          "data.vendor",
          "data.product_type",
          "data.options",
          "data.fake_discount_percentage",
          "data.fake_discount_message",
          "data.created_at",
          "data.updated_at",
          "data.published_at",
          "data.created_date",
          "variations",
        ],
      };

      Object.assign(browseParams, cleanOptions);

      const response = await this.client.browse.getBrowseResults("group_id", collectionHandle, browseParams, {
        timeout: 10000,
      });

      if (mySequence !== this.requestSequence) {
        return false;
      }

      const results = response.response?.results || response.results || [];

      this.currentFacets = response.response?.facets || [];
      this.currentSortOptions = response.response?.sort_options || [];

      this.updateSortDropdown();
      this.loadConstructorFilters();

      if (options.appendToExisting && results.length === 0) {
        this.hideLoadMore();
        return true;
      }

      const constructorProducts = results
        .map((item) => {
          const handle =
            item.data?.url?.split("/products/")[1]?.split("?")[0] ||
            item.data?.handle ||
            item.value?.toLowerCase().replace(/\s+/g, "-");

          return {
            handle,
            ...item.data,
            variations: item.variations ?? [],
            value: item.value,
            matchedTerms: item.matched_terms ?? [],
            isSlotted: item.is_slotted ?? false,
            constructorData: item,
          };
        })
        .filter((item) => item.handle);

      const productHandles = constructorProducts.map((item) => item.handle);

      this.totalResultsFromAPI = response.response?.total_num_results || results.length;

      await this.renderProducts(productHandles, {
        collectionHandle: collectionHandle,
        resultId: response.result_id,
        totalResults: this.totalResultsFromAPI,
        constructorProducts: constructorProducts,
        currentPage: currentPage,
        resultsPerPage: resultsPerPage,
        ...options,
      });

      this.updateURL(collectionHandle, {
        page: currentPage,
        resultsPerPage: resultsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder,
        filters: this.currentFilters,
      });

      if (!options.appendToExisting) {
        this.renderLoadMore({
          currentPage: currentPage,
          resultsPerPage: resultsPerPage,
          totalResults: response.response?.total_num_results || results.length,
          collectionHandle: collectionHandle,
        });
      } else {
        setTimeout(() => {
          this.renderLoadMore({
            currentPage: currentPage,
            resultsPerPage: resultsPerPage,
            totalResults: response.response?.total_num_results || results.length,
            collectionHandle: collectionHandle,
          });
        }, 100);
      }

      return true;
    } catch (error) {
      if (error.name === "NetworkError" || error.code === "ECONNABORTED") {
        const retryDelay = Math.min(1000 * Math.pow(2, this.retryCount), 10000);
        this.retryCount++;

        if (this.retryCount <= 3) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return this.handleBrowse(collectionHandle, options);
        }
      }

      this.retryCount = 0;

      this.showErrorState();

      return false;
    }
  }

  async renderProducts(handles, options = {}) {
    if (!handles || handles.length === 0) {
      this.showNoResults(options.collectionHandle);
      return;
    }

    try {
      const constructorProducts = options.constructorProducts || [];

      if (constructorProducts.length === 0) {
        this.showNoResults(options.collectionHandle);
        return;
      }

      const combinedProducts = constructorProducts.map((constructorProduct) => {
        const data = constructorProduct.constructorData?.data || {};
        const variations = constructorProduct.variations || [];

        return {
          handle: constructorProduct.handle,
          title: constructorProduct.value || data.title || "Product",
          url: data.url || `/products/${constructorProduct.handle}`,
          available: (data.inventory_quantity || 0) > 0,
          id: data.id,
          price: Math.round((data.price || 0) * 100),
          compare_at_price: Math.round((data.compareAtPrice || 0) * 100),
          featured_image: {
            alt: constructorProduct.value || data.title || "Product",
            src: data.image_url || "",
          },
          images: data.mediaImages || [],
          vendor: data.vendor || "",
          product_type: data.product_type || "",
          has_only_default_variant: variations.length <= 1,
          options: (() => {
            const optionsMap = new Map();
            variations.forEach((variation) => {
              const varData = variation.data || {};
              if (varData.color) {
                if (!optionsMap.has("Color")) {
                  optionsMap.set("Color", { name: "Color", values: new Set() });
                }
                optionsMap.get("Color").values.add(varData.color);
              }
              if (varData.size) {
                if (!optionsMap.has("Size")) {
                  optionsMap.set("Size", { name: "Size", values: new Set() });
                }
                optionsMap.get("Size").values.add(varData.size);
              }
            });
            return Array.from(optionsMap.entries()).map(([name, option]) => ({
              name,
              values: Array.from(option.values),
            }));
          })(),
          variants: variations?.map((v) => this._transformVariant(v, data)) || [],
          constructorData: constructorProduct.constructorData,
          variations: variations,
          matchedTerms: constructorProduct.matchedTerms || [],
          isSlotted: constructorProduct.isSlotted || false,
          tags: [
            ...(data.group_ids || []),
            ...(data.tags || []),
            data.preorder_tag && "preorder",
            data.waterproof_tag && "waterproof",
            data.bestseller_tag && "bestseller",
            data.new_tag && "new",
          ].filter(Boolean),
        };
      });

      const productHTML = this.renderProductsTemplate(combinedProducts, options);

      const resultsContainer = this.getResultsContainer();

      if (!resultsContainer) {
        this.showErrorState();
        return;
      }

      const existingGrid = resultsContainer.querySelector("[data-cnstrc-browse]");
      let finalShowingCount;

      if (existingGrid) {
        if (options.appendToExisting) {
          existingGrid.insertAdjacentHTML("beforeend", productHTML);
          const currentCount = parseInt(existingGrid.getAttribute("data-cnstrc-num-results")) || 0;
          const newTotalCount = currentCount + combinedProducts.length;
          existingGrid.setAttribute("data-cnstrc-num-results", newTotalCount);
          finalShowingCount = newTotalCount;
        } else {
          existingGrid.classList.add("constructor-ready");
          existingGrid.innerHTML = "";
          existingGrid.insertAdjacentHTML("beforeend", productHTML);
          existingGrid.setAttribute("data-cnstrc-num-results", combinedProducts.length);

          const currentPage = options.currentPage || 1;
          const resultsPerPage = options.resultsPerPage || 40;
          const totalResults = options.totalResults || this.totalResultsFromAPI || combinedProducts.length;
          finalShowingCount = Math.min(currentPage * resultsPerPage, totalResults);
        }
        existingGrid.style.display = "";
        existingGrid.classList.add("constructor-loaded");

        existingGrid.querySelectorAll("li.d-none, li.shopify-fallback").forEach((item) => {
          item.remove();
        });
      } else {
        const gridHTML = `
          <ul class="product-list list-unstyled row mx-n3 mx-lg-n4 mb-0 constructor-loaded constructor-ready" 
              role="list"
              aria-label="Products in this collection"
              data-cnstrc-browse 
              data-cnstrc-collection-handle="${options.collectionHandle || ""}"
              data-cnstrc-num-results="${combinedProducts.length}"
              data-cnstrc-result-id="${options.resultId || ""}">
            ${productHTML}
          </ul>
        `;
        resultsContainer.innerHTML = gridHTML;
        finalShowingCount = combinedProducts.length;
      }

      this.updateResultsMessage(options.collectionHandle, finalShowingCount);

      const finalGrid = resultsContainer.querySelector("[data-cnstrc-browse]");
      if (finalGrid && options.resultId) {
        finalGrid.setAttribute("data-cnstrc-result-id", options.resultId);
      }

      const fallbackPagination = resultsContainer.querySelector(".constructor-fallback-pagination");
      if (fallbackPagination) {
        fallbackPagination.style.display = "none";
      }

      this.reinitializeComponents(resultsContainer);

      document.dispatchEvent(
        new CustomEvent("constructor:browse:results", {
          detail: {
            numResults: combinedProducts.length,
            collectionHandle: options.collectionHandle,
            appendToExisting: options.appendToExisting || false,
          },
        })
      );
    } catch (error) {
      this.showErrorState();
    }
  }

  renderProductsTemplate(products, options = {}) {
    if (!products || products.length === 0) {
      return '<li class="col-12"><p class="text-center">No products found.</p></li>';
    }

    const productHTML = products
      .map((product, index) => {
        return this.renderSingleProductTemplate(product, { ...options, productIndex: index });
      })
      .join("");

    return productHTML;
  }

  productHasTag(product, requiredTag) {
    if (!requiredTag) return false;

    const data = product.constructorData?.data || {};

    const tagMap = {
      preorder: data.preorder_tag,
      "pre-order": data.preorder_tag,
      waterproof: data.waterproof_tag,
      bestseller: data.bestseller_tag,
      "best-seller": data.bestseller_tag,
      bestsellers: data.bestseller_tag,
      new: data.new_tag,
      "low-inventory": data.low_inventory_tag,
    };

    if (tagMap[requiredTag] !== undefined) {
      return tagMap[requiredTag] === true;
    }

    if (data.tags) {
      return data.tags.includes(requiredTag);
    }

    if (data.group_ids && Array.isArray(data.group_ids)) {
      return data.group_ids.includes(requiredTag);
    }

    return false;
  }

  generateSwatchesFromConstructorData(swatchValues, uniqueColors, swatchSettings) {
    const maxVisible = swatchSettings.maxVisible || 4;

    let swatchesHTML = `
      <div class="swatches-container">
        <ul class="color-swatches list-unstyled mx-0 mb-5" role="list" aria-label="Color options">
    `;

    let visibleSwatchCount = 0;

    swatchValues.forEach((colorValue) => {
      const variationData = uniqueColors.get(colorValue);

      if (!variationData || visibleSwatchCount >= maxVisible) return;

      const isVisible = visibleSwatchCount < maxVisible;
      visibleSwatchCount++;

      swatchesHTML += `
        <li class="p-1" role="listitem" ${isVisible ? "" : "hidden"}>
          <button class="btn${(variationData.inventory_quantity || 0) > 0 ? "" : " swatch-unavailable"}" type="button"
                  data-img="${variationData.image_url || ""}"
                  data-img-width="600"
                  data-img-height="600" 
                  data-img-thumbnail="${window.theme?.productCard?.imgThumbnail || ""}"
                  data-variant-available="${(variationData.inventory_quantity || 0) > 0}"
                  data-color-value="${colorValue}"
                  data-action="change-swatch"
                  aria-label="Select ${colorValue} color option"
                  title="${colorValue}">
      `;

      if (swatchSettings.useVariantImages && variationData.image_url) {
        swatchesHTML += `
          <img class="img-fluid swatch-variant-image"
               src="${variationData.image_url}"
               alt="${colorValue}"
               width="40" height="40"
               loading="lazy" aria-hidden="true">
        `;
      } else {
        const swatchImageName = colorValue.toLowerCase().replace(/\s+/g, "-");
        const shopifySwatchUrl = `https://cdn.shopify.com/s/files/1/0262/9903/9821/files/swatch-${swatchImageName}.png?width=24&height=24&crop=center`;

        swatchesHTML += `
          <img class="img-fluid swatch-custom-image"
               src="${shopifySwatchUrl}"
               alt="${colorValue}"
               width="24" height="24"
               loading="lazy" aria-hidden="true"
               onerror="this.style.display='none'">
        `;
      }

      swatchesHTML += `
          </button>
        </li>
      `;
    });

    if (swatchValues.length > maxVisible) {
      const remainingCount = swatchValues.length - maxVisible;
      swatchesHTML += `
        <li class="color-swatch-more" role="listitem">
          <button type="button" 
                  class="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                  style="width: 32px; height: 32px; font-size: 10px;"
                  title="Show ${remainingCount} more colors"
                  aria-label="Show ${remainingCount} more color options"
                  data-action="show-all-swatches">
            +${remainingCount}
          </button>
        </li>
      `;
    }

    swatchesHTML += `
        </ul>
      </div>
    `;

    return {
      shouldShow: true,
      swatchesHTML: swatchesHTML,
      totalColors: swatchValues.length,
      visibleColors: Math.min(swatchValues.length, maxVisible),
    };
  }

  formatMoney(cents, options = {}) {
    return ConstructorUtils.formatMoney(cents, options);
  }

  formatMoneyWithCurrency(cents) {
    return ConstructorUtils.formatMoneyWithCurrency(cents);
  }

  formatMoneySimple(cents) {
    return ConstructorUtils.formatMoney(cents);
  }

  getSmartVariantSwatches(product) {
    return ConstructorUtils.generateVariantSwatches(product);
  }

  getSmartPrimaryBadge(product, hasDiscount) {
    return ConstructorUtils.generateProductBadge(product, hasDiscount);
  }

  /**
   * Generate responsive images with CDN optimization and srcset
   * @param {string} primarySrc - Primary image URL
   * @param {string} secondarySrc - Secondary image URL
   * @param {number} baseWidth - Base width for aspect ratio
   * @param {number} baseHeight - Base height for aspect ratio
   * @returns {Object} - Optimized image URLs and srcset
   */
  generateResponsiveImages(primarySrc, secondarySrc, baseWidth, baseHeight) {
    const cdnParams = "crop=center&format=webp&quality=80";

    // Mobile-first responsive breakpoints
    const breakpoints = [
      { width: 300, condition: "(max-width: 480px)" }, // Mobile phones
      { width: 400, condition: "(max-width: 768px)" }, // Tablets portrait
      { width: 500, condition: "(max-width: 992px)" }, // Tablets landscape
      { width: 600, condition: "(min-width: 993px)" }, // Desktop
    ];

    const generateImageSet = (imageSrc) => {
      if (!imageSrc) return { src: "", srcset: "", sizes: "" };

      // Handle both Shopify CDN and external URLs
      const isShopifyCDN = imageSrc.includes("shopifycdn.com") || imageSrc.includes("cdn.shopify.com");

      if (!isShopifyCDN) {
        // For external URLs, return as-is (Constructor.io already optimizes)
        return {
          src: imageSrc,
          srcset: imageSrc,
          sizes: "100vw",
        };
      }

      // Remove existing parameters to avoid conflicts
      const baseUrl = imageSrc.split("?")[0];

      // Generate srcset with 1x and 2x variants for each breakpoint
      const srcsetEntries = [];
      breakpoints.forEach((bp) => {
        const height = Math.round(bp.width * (baseHeight / baseWidth));
        // 1x density
        srcsetEntries.push(`${baseUrl}?width=${bp.width}&height=${height}&${cdnParams} ${bp.width}w`);
        // 2x density for retina
        const width2x = bp.width * 2;
        const height2x = height * 2;
        srcsetEntries.push(`${baseUrl}?width=${width2x}&height=${height2x}&${cdnParams} ${width2x}w`);
      });

      // Default src for fallback (mobile size)
      const defaultHeight = Math.round(300 * (baseHeight / baseWidth));
      const defaultSrc = `${baseUrl}?width=300&height=${defaultHeight}&${cdnParams}`;

      // Sizes attribute for responsive loading
      const sizes = ["(max-width: 480px) 100vw", "(max-width: 768px) 50vw", "(max-width: 992px) 33vw", "25vw"].join(", ");

      return {
        src: defaultSrc,
        srcset: srcsetEntries.join(", "),
        sizes: sizes,
      };
    };

    const primary = generateImageSet(primarySrc);
    const secondary = generateImageSet(secondarySrc);

    return {
      primarySrc: primary.src,
      primarySrcset: primary.srcset,
      secondarySrc: secondary.src,
      secondarySrcset: secondary.srcset,
      sizes: primary.sizes,
    };
  }

  /**
   * Transform Constructor.io variation to Shopify variant format
   * @private
   */
  _transformVariant(variant, parentData) {
    const variantTitle = variant.data?.title || variant.value || "Variant";

    let color = variant.data?.color;
    if (!color && variantTitle) {
      const titleParts = variantTitle.split(/[\/,]/).map((s) => s.trim());
      color = titleParts[0];
    }

    return {
      id: variant.data?.variation_id || variant.id,
      title: variantTitle,
      price: Math.round((variant.data?.price || parentData.price || 0) * 100),
      compare_at_price: Math.round((variant.data?.compareAtPrice || parentData.compareAtPrice || 0) * 100),
      inventory_quantity: variant.data?.inventory_quantity || 0,
      available: (variant.data?.inventory_quantity || 0) > 0,
      image_url: variant.data?.image_url,
      color: color,
      size: variant.data?.size,
      discountPercentage: variant.data?.discountPercentage || variant.data?.fake_discount_percentage || 0,
      featured_image: { src: variant.data?.image_url || parentData.image_url || "" },
      inventory_policy: variant.data?.inventory_policy || "deny",
    };
  }

  renderSingleProductTemplate(product, options = {}) {
    if (!product) return "";

    const productTitle = product.title || "";
    const productHandle = product.handle || "";
    const productUrl = product.url || product.constructorData?.url || `/products/${productHandle}`;
    const firstVariant = (product.variants && product.variants[0]) || {};
    const isAvailable = product.available || false;

    const imgOrientation = window.theme?.productCard?.imgOrientation || "square";
    let imgWidth, imgHeight, aspectRatio;

    switch (imgOrientation) {
      case "square":
        imgWidth = 600;
        imgHeight = 600;
        aspectRatio = "1/1";
        break;
      case "landscape":
        imgWidth = 600;
        imgHeight = 480;
        aspectRatio = "5/4";
        break;
      case "portrait":
        imgWidth = 480;
        imgHeight = 600;
        aspectRatio = "4/5";
        break;
      default:
        imgWidth = 600;
        imgHeight = 600;
        aspectRatio = "1/1";
    }

    const constructorData = product.constructorData?.data || product.constructorData || {};

    const mediaImages =
      Array.isArray(product.images) && product.images.length > 0
        ? product.images
        : Array.isArray(constructorData.mediaImages)
        ? constructorData.mediaImages
        : [];

    const rawPrimarySrc =
      this.getImageUrl(constructorData) || mediaImages[0]?.transformedSrc || product.featured_image?.src || "";
    const rawSecondarySrc = this.getSecondaryImageUrl(constructorData) || mediaImages[1]?.transformedSrc || null;

    // Generate optimized responsive images with CDN parameters
    const { primarySrc, primarySrcset, secondarySrc, secondarySrcset, sizes } = this.generateResponsiveImages(
      rawPrimarySrc,
      rawSecondarySrc,
      imgWidth,
      imgHeight
    );

    // Determine loading strategy - first few cards get priority
    const productIndex = options.productIndex || 0;
    const isAboveTheFold = productIndex < 6; // First 6 products above fold
    const loadingStrategy = isAboveTheFold ? "eager" : "lazy";
    const fetchPriority = isAboveTheFold && productIndex < 2 ? "high" : "auto";
    const imageAlt = mediaImages[0]?.altText || product.featured_image?.alt || productTitle;

    const finalPrice = Math.round((constructorData.price || firstVariant.price || 0) * 100);
    const originalPrice = Math.round((constructorData.compareAtPrice || firstVariant.compare_at_price || 0) * 100);
    const discountPercentage = constructorData.discountPercentage || 0;
    const isOnSale = originalPrice && originalPrice > finalPrice;

    let discountMessage = null;
    if (discountPercentage && discountPercentage >= 50 && window.theme?.fakeDiscount?.marketSettings?.enabled) {
      const settings = window.theme.fakeDiscount.marketSettings;
      discountMessage = settings.message?.replace(/\[percentage\]/g, discountPercentage) || "";
    }

    const primaryBadgeResult = this.getSmartPrimaryBadge(product, isOnSale, discountPercentage > 0);
    const showSaleBadge = window.theme?.productCard?.showSaleBadge && isOnSale;
    const saleBadgeColor = window.theme?.productCard?.saleBadgeColor || "primary";

    const swatchesResult = this.getSmartVariantSwatches(product);

    const formattedPrice = this.formatMoneyWithCurrency(finalPrice);
    const formattedComparePrice = isOnSale && originalPrice ? this.formatMoneyWithCurrency(originalPrice) : "";

    const cardStyle = window.theme?.productCard?.style;
    const spacingMobile = window.theme?.productCard?.spacing?.mobile ? `p-${window.theme.productCard.spacing.mobile}` : "p-3";
    const spacingDesktop = window.theme?.productCard?.spacing?.desktop
      ? `p-lg-${window.theme.productCard.spacing.desktop}`
      : "p-lg-4";

    const imgThumbnail = window.theme?.productCard?.imgThumbnail || "";

    const marginClass = cardStyle !== "card" ? "mb-4" : "";

    return `
      <li class="product-item splide__slide ${spacingMobile} ${spacingDesktop} ${marginClass}" 
          role="listitem"
          data-product-handle="${productHandle}"
          data-cnstrc-item-id="${product.data?.id || product.id}"
          data-cnstrc-item-name="${ConstructorUtils.sanitizeHTML(productTitle)}"
          data-cnstrc-item-variation-id="${firstVariant.id || ""}"
          data-cnstrc-item-price="${finalPrice / 100}">
        <div class="${cardStyle || ""}">
          <div class="img-wrapper position-relative mb-4${secondarySrc ? " has-hover-image" : ""}">
            <a href="${productUrl}">
              ${
                primaryBadgeResult.shouldShow
                  ? primaryBadgeResult.badgeHTML
                  : showSaleBadge && discountPercentage > 0
                  ? `<span class="img-badge-sale badge ${saleBadgeColor} text-white position-absolute" style="z-index: 1;">
                       ${window.theme?.product?.save || "Save"} ${discountPercentage}%
                     </span>`
                  : ""
              }
              ${
                primarySrc
                  ? `
                <img 
                  src="${primarySrc}"
                  srcset="${primarySrcset}"
                  sizes="${sizes}"
                  alt="${imageAlt}"
                  class="product-item-img product-item-img--primary img-fluid rounded ${imgThumbnail} png-background"
                  width="${imgWidth}"
                  height="${imgHeight}"
                  loading="${loadingStrategy}"
                  fetchpriority="${fetchPriority}"
                  decoding="async"
                  style="aspect-ratio: ${aspectRatio};"
                />
                ${
                  secondarySrc
                    ? `
                <img 
                  src="${secondarySrc}"
                  srcset="${secondarySrcset}"
                  sizes="${sizes}"
                  alt="${imageAlt}"
                  class="product-item-img product-item-img--secondary img-fluid rounded ${imgThumbnail} png-background"
                  width="${imgWidth}"
                  height="${imgHeight}"
                  loading="lazy"
                  fetchpriority="low"
                  decoding="async"
                  style="aspect-ratio: ${aspectRatio};"
                />`
                    : ""
                }
              `
                  : `
                <div class="placeholder-image bg-light d-flex align-items-center justify-content-center" 
                     style="aspect-ratio: ${aspectRatio}; min-height: 200px;">
                  <span class="text-muted">No Image</span>
                </div>
              `
              }
            </a>

            ${this.getSmartAddToCartForm(product)}
          </div>

            ${
              window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
                ? `
          <div class="wishlist-below-image mt-2 d-flex justify-content-end">
            <button class="btn-wishlist-add-remove btn" 
                    type="button"
                    data-product-handle="${productHandle}"
                    data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                    data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                    aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                    onclick="addOrRemoveFromWishlist(this)"
                    data-cnstrc-btn="add_to_wishlist">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" 
                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
          </div>
            `
                : ""
            }
          
          <div class="firstline-container">
            <div class="${cardStyle === "card" ? "card-body" : ""}">
              <h3 class="product-item-title mb-2 ${window.theme?.productCard?.titleFontSizeMobile || "fs-6"} ${
      window.theme?.productCard?.titleFontSizeDesktop || "fs-lg-6"
    } ${window.theme?.productCard?.titleTruncate || ""}">
                <a href="${productUrl}" class="product-link position-relative d-block link-dark" tabindex="-1">
                  ${ConstructorUtils.sanitizeHTML(productTitle)}
                </a>
              </h3>
              
              ${window.theme?.productCard?.showRating ? `<div class="product-rating-badge mb-2"></div>` : ""}
              
              <p class="product-item-price product-item-price-carrossel mb-2" data-product-item-price>
              ${
                isOnSale
                  ? `
                    <span class="product-item-price-compare text-muted me-1">
                      <span class="visually-hidden">${window.theme?.product?.priceRegular || "Regular price"}&nbsp;</span>
                      <s>${formattedComparePrice}</s>
                    </span>
                    <span class="product-item-price-final">
                      <span class="visually-hidden">${window.theme?.product?.priceSale || "Sale price"}&nbsp;</span>
                      ${formattedPrice}
                    </span>
                  `
                  : `
                    <span class="product-item-price-final">
                      ${formattedPrice}
                    </span>
                  `
              }
            </p>

              ${
                discountMessage
                  ? `
                <div class="fake-discount-message mt-2 p-2 rounded-2" 
                     style="color: ${
                       window.theme?.fakeDiscount?.marketSettings?.messageColor || "#ff0000"
                     }; font-size: 0.875rem; line-height: 1.2;">
                  ${discountMessage}
                </div>
              `
                  : ""
              }

              ${swatchesResult.shouldShow ? swatchesResult.swatchesHTML : ""}
            </div>
          </div>
          
          ${this.getSmartAddToCartFormMobile(product)}
        </div>
      </li>
    `;
  }

  getResultsContainer() {
    let container = document.querySelector("#constructor-browse-results");
    if (container) {
      return container;
    }

    container = document.querySelector("[data-cnstrc-browse]");
    if (container && container.parentElement) {
      return container.parentElement;
    }

    container = document.querySelector("#collection .container");
    if (container) {
      return container;
    }

    return null;
  }

  updateResultsMessage(totalResults) {
    const messageElement = document.querySelector(".collection-results-message");
    if (messageElement) {
      const totalAvailable = this.totalResultsFromAPI || totalResults;

      const currentLocale = document.documentElement.lang || "en";
      const translations = {
        en: `Showing ${totalResults} of ${totalAvailable} products`,
        pt: `Exibindo ${totalResults} de ${totalAvailable} produtos`,
        es: `Mostrando ${totalResults} de ${totalAvailable} productos`,
        fr: `Affichage de ${totalResults} sur ${totalAvailable} produits`,
        de: `Es werden ${totalResults} von ${totalAvailable} Produkten angezeigt`,
      };

      const message = translations[currentLocale] || translations.en;
      messageElement.textContent = message;
      messageElement.classList.remove("constructor-hide-until-ready");
      messageElement.style.display = "";

      this.updateProgressBar(totalResults, totalAvailable);
    } else {
      console.warn("Constructor.io: collection-results-message element not found");
    }
  }

  updateProgressBar(currentResults, totalResults) {
    const progressBar = document.querySelector("#collection-pagination .progress-bar");
    if (progressBar && totalResults > 0) {
      const percentage = Math.round((currentResults / totalResults) * 100);
      progressBar.style.width = `${percentage}%`;
      progressBar.setAttribute("aria-valuenow", currentResults);
      progressBar.setAttribute("aria-valuemax", totalResults);
    }
  }

  showErrorState() {
    const container = this.getResultsContainer();
    if (container) {
      const tpl = document.getElementById("shopify-fallback-template");
      if (tpl) {
        const ul = document.createElement("ul");
        ul.className = "product-list list-unstyled row mb-0";
        ul.setAttribute("role", "list");
        ul.setAttribute("aria-label", "Products in this collection");

        const clonedContent = tpl.content.cloneNode(true);
        ul.appendChild(clonedContent);

        const existingGrid = container.querySelector("[data-cnstrc-browse]");
        if (existingGrid) {
          existingGrid.replaceWith(ul);
        } else {
          container.innerHTML = "";
          container.appendChild(ul);
        }
      } else {
        const errorEl = document.createElement("div");
        errorEl.className = "alert alert-warning py-3";
        errorEl.setAttribute("role", "status");
        errorEl.textContent = "Search temporarily unavailable. Please try again.";

        const existingGrid = container.querySelector("[data-cnstrc-browse]");
        if (existingGrid) {
          existingGrid.classList.add("constructor-loaded");
          existingGrid.replaceWith(errorEl);
        }
      }
    }

    this.hideLoadMore();
  }

  showConstructorUtilities() {
    const utilities = document.getElementById("constructor-utilities");
    if (utilities) {
      utilities.style.display = "block";
    }
  }

  toggleFilters() {
    const offcanvas = document.getElementById("constructor-offcanvas-filters");

    if (offcanvas) {
      this.loadConstructorFilters();

      const bsOffcanvas = bootstrap.Offcanvas.getOrCreateInstance(offcanvas);
      bsOffcanvas.show();
    }
  }

  async loadConstructorFilters() {
    const filtersBody = document.getElementById("constructor-filters-body");

    if (!filtersBody) {
      return;
    }

    if (this.currentFacets && this.currentFacets.length > 0) {
      const filtersHTML = ConstructorUtils.generateFiltersFromFacets(this.currentFacets, this.currentFilters);
      filtersBody.innerHTML = filtersHTML;
      this.bindFilterEvents();
    } else if (window.Shopify && window.Shopify.designMode) {
      const hardcodedFacets = this.getHardcodedFacets();
      const filtersHTML = ConstructorUtils.generateFiltersFromFacets(hardcodedFacets, this.currentFilters);
      filtersBody.innerHTML = filtersHTML;
      this.bindFilterEvents();
    } else {
      filtersBody.innerHTML = '<p class="text-muted">No filters available</p>';
    }
  }

  getHardcodedFacets() {
    return [
      {
        name: "vendor",
        display_name: "Brand",
        options: [
          { value: "evry-jewels", display_name: "Evry Jewels", count: 0 },
          { value: "gold-collection", display_name: "Gold Collection", count: 0 },
          { value: "silver-collection", display_name: "Silver Collection", count: 0 },
        ],
      },
      {
        name: "product_type",
        display_name: "Product Type",
        options: [
          { value: "necklaces", display_name: "Necklaces", count: 0 },
          { value: "earrings", display_name: "Earrings", count: 0 },
          { value: "bracelets", display_name: "Bracelets", count: 0 },
          { value: "rings", display_name: "Rings", count: 0 },
        ],
      },
      {
        name: "tags",
        display_name: "Material",
        options: [
          { value: "gold", display_name: "Gold", count: 0 },
          { value: "silver", display_name: "Silver", count: 0 },
          { value: "rose-gold", display_name: "Rose Gold", count: 0 },
          { value: "sterling-silver", display_name: "Sterling Silver", count: 0 },
        ],
      },
    ];
  }

  bindFilterEvents() {
    const debouncedApplyFilters = ConstructorUtils.debounce(() => {
      this.applyFilters();
    }, 300);

    const filtersBody = document.getElementById("constructor-filters-body");
    if (filtersBody) {
      const existingChangeHandler = filtersBody.__constructorChangeHandler;
      const existingInputHandler = filtersBody.__constructorInputHandler;
      const existingBlurHandler = filtersBody.__constructorBlurHandler;
      const existingKeydownHandler = filtersBody.__constructorKeydownHandler;

      if (existingChangeHandler) {
        filtersBody.removeEventListener("change", existingChangeHandler);
      }
      if (existingInputHandler) {
        filtersBody.removeEventListener("input", existingInputHandler);
      }
      if (existingBlurHandler) {
        filtersBody.removeEventListener("blur", existingBlurHandler, true);
      }
      if (existingKeydownHandler) {
        filtersBody.removeEventListener("keydown", existingKeydownHandler);
      }

      const changeHandler = (e) => {
        if (e.target.classList.contains("constructor-filter-checkbox")) {
          this.updateCurrentFilters(e.target);
          debouncedApplyFilters();
        }
      };

      const inputHandler = (e) => {
        if (e.target.classList.contains("constructor-price-input")) {
          this.updatePriceFilters(e.target, false);
          e.target.style.borderColor = "#ffc107";
          e.target.style.boxShadow = "0 0 0 0.2rem rgba(255, 193, 7, 0.25)";
        }
      };

      const blurHandler = (e) => {
        if (e.target.classList.contains("constructor-price-input")) {
          const isSuppressed = !!this.suppressBlurApply;

          this.updatePriceFilters(e.target, true);

          const isMin = e.target.getAttribute("data-filter-name") === "price_min";
          const siblingSelector = isMin
            ? '.constructor-price-input[data-filter-name="price_max"]'
            : '.constructor-price-input[data-filter-name="price_min"]';
          const sibling = document.querySelector(siblingSelector);
          if (sibling) {
            this.updatePriceFilters(sibling, true);
          }

          e.target.style.borderColor = "";
          e.target.style.boxShadow = "";
          if (!isSuppressed) {
            debouncedApplyFilters();
          }
        }
      };

      const keydownHandler = (e) => {
        if (e.target.classList.contains("constructor-price-input") && e.key === "Enter") {
          e.target.blur();
        }
      };

      filtersBody.addEventListener("change", changeHandler);
      filtersBody.addEventListener("input", inputHandler);
      filtersBody.addEventListener("blur", blurHandler, true);
      filtersBody.addEventListener("keydown", keydownHandler);

      filtersBody.__constructorChangeHandler = changeHandler;
      filtersBody.__constructorInputHandler = inputHandler;
      filtersBody.__constructorBlurHandler = blurHandler;
      filtersBody.__constructorKeydownHandler = keydownHandler;
    }
  }

  updatePriceFilters(input, normalize = false) {
    const filterName = input.getAttribute("data-filter-name");
    const inputValue = input.value.trim();
    let value = inputValue === "" ? null : parseInt(inputValue);

    delete this.currentFilters.price;

    if (normalize && value !== null && !isNaN(value) && this.priceLimits) {
      const minLimit = this.priceLimits.min;
      const maxLimit = this.priceLimits.max;

      if (filterName === "price_min") {
        if (value < minLimit) value = minLimit;
        if (value > maxLimit) value = maxLimit;
        input.value = value;

        const currentMax = this.currentFilters.price_max ?? maxLimit;
        if (currentMax !== null && !isNaN(currentMax) && value > currentMax) {
          this.currentFilters.price_max = value;
          const maxInput = document.querySelector('.constructor-price-input[data-filter-name="price_max"]');
          if (maxInput) maxInput.value = value;
        }
      } else if (filterName === "price_max") {
        if (value > maxLimit) value = maxLimit;
        if (value < minLimit) value = minLimit;
        input.value = value;

        const currentMin = this.currentFilters.price_min ?? minLimit;
        if (currentMin !== null && !isNaN(currentMin) && value < currentMin) {
          this.currentFilters.price_min = value;
          const minInput = document.querySelector('.constructor-price-input[data-filter-name="price_min"]');
          if (minInput) minInput.value = value;
        }
      }
    }

    if (filterName === "price_min") {
      if (value === null || isNaN(value)) {
        delete this.currentFilters.price_min;
      } else if (value <= 0) {
        delete this.currentFilters.price_min;
        input.value = "";
      } else {
        this.currentFilters.price_min = value;
      }
    } else if (filterName === "price_max") {
      if (value === null || isNaN(value)) {
        delete this.currentFilters.price_max;
      } else if (value <= 0) {
        delete this.currentFilters.price_max;
        input.value = "";
      } else {
        this.currentFilters.price_max = value;
      }
    }
  }

  updateCurrentFilters(checkbox) {
    const filterName = checkbox.getAttribute("data-filter-name");
    const filterValue = checkbox.getAttribute("data-filter-value");

    if (!this.currentFilters[filterName]) {
      this.currentFilters[filterName] = [];
    }

    if (checkbox.checked) {
      if (!this.currentFilters[filterName].includes(filterValue)) {
        this.currentFilters[filterName].push(filterValue);
      }
    } else {
      this.currentFilters[filterName] = this.currentFilters[filterName].filter((value) => value !== filterValue);

      if (this.currentFilters[filterName].length === 0) {
        delete this.currentFilters[filterName];
      }
    }

    const keysToDelete = [];
    Object.keys(this.currentFilters).forEach((key) => {
      const value = this.currentFilters[key];
      if (!value || !Array.isArray(value) || value.length === 0) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      delete this.currentFilters[key];
    });
  }

  applyFiltersWithPriceInputs() {
    const priceInputs = document.querySelectorAll(".constructor-price-input");
    const minInput = document.querySelector('.constructor-price-input[data-filter-name="price_min"]');
    const maxInput = document.querySelector('.constructor-price-input[data-filter-name="price_max"]');

    if (minInput) this.updatePriceFilters(minInput, true);
    if (maxInput) this.updatePriceFilters(maxInput, true);

    priceInputs.forEach((input) => {
      input.style.borderColor = "";
      input.style.boxShadow = "";
    });

    this.applyFilters();
  }

  applyFilters() {
    const collectionHandle = this.currentCollectionHandle || window.cnstrc?.collectionHandle || "";

    if (!collectionHandle) {
      return;
    }

    const cleanFilters = {};
    Object.keys(this.currentFilters).forEach((key) => {
      const value = this.currentFilters[key];

      if (Array.isArray(value) && value.length > 0) {
        cleanFilters[key] = value;
      } else if ((key === "price_min" || key === "price_max") && value !== "" && value !== null && value !== undefined) {
        cleanFilters[key] = value;
      }
    });

    if (cleanFilters.price_min || cleanFilters.price_max) {
      if (cleanFilters.price_min && cleanFilters.price_max) {
        cleanFilters.price = `${cleanFilters.price_min}-${cleanFilters.price_max}`;
      } else if (cleanFilters.price_min && !cleanFilters.price_max) {
        const maxLimit = this.priceLimits ? this.priceLimits.max : 999999;
        cleanFilters.price = `${cleanFilters.price_min}-${maxLimit}`;
      } else if (!cleanFilters.price_min && cleanFilters.price_max) {
        const minLimit = this.priceLimits ? this.priceLimits.min : 0;
        cleanFilters.price = `${minLimit}-${cleanFilters.price_max}`;
      }

      delete cleanFilters.price_min;
      delete cleanFilters.price_max;
    }

    this.currentFilters = cleanFilters;

    const urlParams = new URLSearchParams(window.location.search);
    const hasNoFilters = Object.keys(cleanFilters).length === 0;
    const currentResultsPerPage = parseInt(urlParams.get("resultsPerPage")) || 40;

    const resultsPerPageToUse = hasNoFilters ? 40 : currentResultsPerPage;

    this.handleBrowse(collectionHandle, {
      filters: cleanFilters,
      page: 1,
      resultsPerPage: resultsPerPageToUse,
    });
  }

  showNoResults(collectionHandle) {
    const container = this.getResultsContainer();
    if (container) {
      const noResultsEl = document.createElement("p");
      noResultsEl.className = "alert alert-warning py-3";
      noResultsEl.setAttribute("role", "status");
      noResultsEl.setAttribute("data-cnstrc-zero-result", "");
      noResultsEl.textContent = `No products found in collection "${collectionHandle}"`;

      const existingGrid = container.querySelector("[data-cnstrc-browse]");
      if (existingGrid) {
        existingGrid.classList.add("constructor-loaded");
        existingGrid.replaceWith(noResultsEl);
      }
    }

    this.hideLoadMore();
  }

  reinitializeComponents(container) {
    document.dispatchEvent(
      new CustomEvent("constructor:products-loaded", {
        detail: { container },
      })
    );

    if (window.ProductCard) {
      container.querySelectorAll(".product-item").forEach((card) => {
        new ProductCard(card);
      });
    }
  }

  updateURL(collectionHandle, params = {}) {
    const url = new URL(window.location);

    if (params.page && params.page > 1) {
      url.searchParams.set("page", params.page);
    } else {
      url.searchParams.delete("page");
    }

    if (params.resultsPerPage && params.resultsPerPage !== 40) {
      url.searchParams.set("resultsPerPage", params.resultsPerPage);
    } else {
      url.searchParams.delete("resultsPerPage");
    }

    if (params.sortBy && params.sortBy !== "relevance") {
      url.searchParams.set("sortBy", params.sortBy);
      url.searchParams.set("sort", `${params.sortBy}:${params.sortOrder || "descending"}`);
    } else {
      url.searchParams.delete("sortBy");
      url.searchParams.delete("sort");
    }

    if (params.sortOrder && params.sortOrder !== "descending") {
      url.searchParams.set("sortOrder", params.sortOrder);
    } else {
      url.searchParams.delete("sortOrder");
    }

    const hasActiveFilters =
      params.filters &&
      Object.keys(params.filters).some((key) => {
        const value = params.filters[key];
        return Array.isArray(value) && value.length > 0;
      });

    if (hasActiveFilters) {
      url.searchParams.set("filters", encodeURIComponent(JSON.stringify(params.filters)));
    } else {
      url.searchParams.delete("filters");
    }
    window.history.pushState(null, "", url.toString());
  }

  renderLoadMore(options) {
    const { currentPage, resultsPerPage, totalResults, collectionHandle } = options;

    const existingGrid = this.getResultsContainer()?.querySelector("[data-cnstrc-browse]");
    const currentlyDisplayed = existingGrid ? parseInt(existingGrid.getAttribute("data-cnstrc-num-results")) || 0 : 0;

    const hasMoreResults = currentlyDisplayed < totalResults;

    if (!hasMoreResults) {
      this.hideLoadMore();
      return;
    }

    let loadMoreContainer = document.querySelector(".constructor-load-more");
    if (!loadMoreContainer) {
      const resultsContainer = this.getResultsContainer();
      if (resultsContainer) {
        loadMoreContainer = document.createElement("div");
        loadMoreContainer.className = "constructor-load-more d-flex justify-content-center mt-4";
        resultsContainer.appendChild(loadMoreContainer);
      }
    }

    if (!loadMoreContainer) return;

    const loadMoreHTML = this.generateLoadMoreHTML(currentPage, collectionHandle, resultsPerPage);
    loadMoreContainer.innerHTML = loadMoreHTML;

    this.bindLoadMoreEvents(loadMoreContainer);

    loadMoreContainer.style.display = "flex";
  }

  generateLoadMoreHTML(currentPage, collectionHandle, resultsPerPage) {
    const nextPage = currentPage + 1;

    return `
      <button
        class="btn-load-more btn btn-outline-primary d-inline-flex align-items-center mt-5 mb-0 constructor-load-more-btn"
        data-next-page="${nextPage}"
        data-collection-handle="${collectionHandle}"
        data-per-page="${resultsPerPage}"
        data-action="load-more"
        aria-label="Load more products">
        ${this.getLoadMoreButtonContent()}
      </button>
    `;
  }

  getLoadMoreButtonContent(isLoading = false) {
    if (isLoading) {
      return `
        <div class="spinner-border spinner-border-sm me-2" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        Loading...
      `;
    }

    return `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="me-2"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round">
        <path d="M7 13l5 5 5-5M7 6l5 5 5-5"/>
      </svg>
      Load More Products
    `;
  }

  setLoadMoreButtonState(isLoading = false, disabled = false) {
    const loadMoreBtn = document.querySelector(".constructor-load-more-btn");
    if (loadMoreBtn) {
      loadMoreBtn.disabled = disabled;
      loadMoreBtn.innerHTML = this.getLoadMoreButtonContent(isLoading);
    }
  }

  bindLoadMoreEvents(container) {
    const loadMoreBtn = container.querySelector(".constructor-load-more-btn");
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const nextPage = parseInt(loadMoreBtn.dataset.nextPage);
        const collectionHandle = loadMoreBtn.dataset.collectionHandle;
        const perPage = parseInt(loadMoreBtn.dataset.perPage);

        this.loadMoreProducts(collectionHandle, nextPage, perPage);
      });
    }
  }

  hideLoadMore() {
    const loadMoreContainer = document.querySelector(".constructor-load-more");
    if (loadMoreContainer) {
      loadMoreContainer.style.display = "none";
    }
  }

  updateSortDropdown() {
    if (!this.currentSortOptions || this.currentSortOptions.length === 0) {
      return;
    }

    const sortDropdown = document.getElementById("sort-dropdown");
    const currentSortDisplay = document.getElementById("current-sort");

    if (!sortDropdown) {
      return;
    }

    const sortHTML = ConstructorUtils.generateSortOptionsFromData(this.currentSortOptions);
    sortDropdown.innerHTML = sortHTML;

    sortDropdown.querySelectorAll(".dropdown-item").forEach((dropdownItem) => {
      dropdownItem.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        document.querySelectorAll("#sort-dropdown .dropdown-item").forEach((item) => item.classList.remove("selected"));
        dropdownItem.classList.add("selected");

        if (currentSortDisplay) {
          currentSortDisplay.textContent = dropdownItem.textContent;
        }

        sortDropdown.style.display = "none";

        this.handleBrowse(this.currentCollectionHandle, {
          sortBy: dropdownItem.getAttribute("data-value"),
          sortOrder: dropdownItem.getAttribute("data-order"),
          filters: this.currentFilters,
        });
      });
    });

    const selectedOption = this.currentSortOptions.find((opt) => opt.status === "selected");
    if (selectedOption && currentSortDisplay) {
      currentSortDisplay.textContent = selectedOption.display_name;
    }
  }

  async loadMoreProducts(collectionHandle, nextPage, resultsPerPage) {
    // Set loading state
    this.setLoadMoreButtonState(true, true);

    const success = await this.handleBrowse(collectionHandle, {
      page: nextPage,
      resultsPerPage: resultsPerPage,
      appendToExisting: true,
    });

    // Reset to normal state (same for success or failure)
    this.setLoadMoreButtonState(false, false);

    return success;
  }

  bindEvents() {
    document.addEventListener("click", (e) => {
      const swatch = e.target.closest('[data-action="change-swatch"]');
      if (!swatch) return;

      e.preventDefault();
      e.stopPropagation();

      const productItem = swatch.closest(".product-item");
      if (!productItem) return;

      const allSwatches = productItem.querySelectorAll(".color-swatches .btn");
      allSwatches.forEach((s) => s.classList.remove("selected"));
      swatch.classList.add("selected");

      const img = productItem.querySelector(".product-item-img--primary");
      if (img) {
        const rawSrc = swatch.getAttribute("data-img");

        if (rawSrc) {
          img.src = rawSrc;
        }
      }
    });

    window.addEventListener("popstate", () => {
      const urlParams = new URLSearchParams(window.location.search);

      const page = parseInt(urlParams.get("page")) || 1;
      const resultsPerPage = parseInt(urlParams.get("resultsPerPage")) || 40;

      let sortBy = urlParams.get("sortBy");
      let sortOrder = urlParams.get("sortOrder");

      const legacySort = urlParams.get("sort");
      if (!sortBy && legacySort && legacySort.includes(":")) {
        const [sb, so] = legacySort.split(":");
        sortBy = sb || "relevance";
        sortOrder = so || "descending";
      }
      sortBy = sortBy || "relevance";
      sortOrder = sortOrder || "descending";

      const urlFilters = urlParams.get("filters");
      let filters = {};
      if (urlFilters) {
        try {
          filters = JSON.parse(decodeURIComponent(urlFilters));
        } catch (e) {
          console.warn("Invalid filters in URL, ignoring:", e);
        }
      }

      if (this.currentCollectionHandle) {
        this.handleBrowse(this.currentCollectionHandle, {
          page: page,
          resultsPerPage: resultsPerPage,
          sortBy: sortBy,
          sortOrder: sortOrder,
          filters: filters,
        });
      }
    });
  }

  handleFilterChange() {
    if (this.currentCollectionHandle) {
      this.handleBrowse(this.currentCollectionHandle, {
        filters: this.currentFilters,
      });
    }
  }

  getSmartAddToCartForm(product, deviceType = "desktop") {
    const data = product.constructorData?.data || {};
    const variants = product.variants || [];

    const settings = window.theme?.productCard;
    if (!settings?.showAtcForm) {
      return "";
    }

    // Check if product is available
    if (!product.available || (data.inventory_quantity !== undefined && data.inventory_quantity <= 0)) {
      return this.generateSoldOutButton(deviceType);
    }

    if (this.productHasTag(product, "customized")) {
      return this.generateViewProductButton(product, deviceType);
    }

    const availableVariants = variants.filter((v) => v.available !== false);

    if (availableVariants.length === 0) {
      return this.generateSoldOutButton(deviceType);
    } else if (availableVariants.length === 1) {
      return this.generateSimpleAddToCartButton(product, availableVariants[0], deviceType);
    }

    return this.generateDropdownAddToCartButton(product, availableVariants, deviceType);
  }

  getSmartAddToCartFormMobile(product) {
    const data = product.constructorData?.data || {};
    const variants = product.variants || [];

    const settings = window.theme?.productCard;
    if (!settings?.showAtcForm) {
      return "";
    }

    if (!product.available || (data.inventory_quantity !== undefined && data.inventory_quantity <= 0)) {
      return this.generateSoldOutButtonMobile();
    }

    if (this.productHasTag(product, "customized")) {
      return this.generateViewProductButtonMobile(product);
    }

    const availableVariants = variants.filter((v) => v.available !== false);

    if (availableVariants.length === 0) {
      return this.generateSoldOutButtonMobile();
    } else if (availableVariants.length === 1) {
      return this.generateSimpleAddToCartButtonMobile(product, availableVariants[0]);
    }

    return this.generateDropdownAddToCartButtonMobile(product, availableVariants);
  }

  generateViewProductButton(product) {
    const translations = window.theme?.productCard?.translations;
    const productUrl = product.url || product.constructorData?.url || `/products/${product.handle}`;

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <a href="${productUrl}" 
             class="view-product-btn-desktop"
             data-action="view-product"
             role="button">
            ${translations?.viewProduct || "View Product"}
          </a>
        </div>
      </div>
    `;
  }

  generateSoldOutButton() {
    const translations = window.theme?.productCard?.translations;

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <button class="btn-atc btn w-100 btn-primary" data-action="sold-out" disabled>
            ${translations?.soldOut || "Sold Out"}
          </button>
        </div>
      </div>
    `;
  }

  generateSimpleAddToCartButton(product, variant) {
    const translations = window.theme?.productCard?.translations;

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
                data-product-url="${product.url || `/products/${product.handle}`}">
            <input type="hidden" name="id" value="${variant.id}">
            <button class="btn-atc btn btn-md w-100 btn-primary"
                    type="submit"
                    data-action="add-to-cart-simple"
                    name="add"
                    data-cnstrc-btn="add_to_cart"
                    data-text-add-to-cart="${translations?.addToCart || "Add To Cart"}">
              ${translations?.addToCart || "Add To Cart"}
            </button>
          </form>
        </div>
      </div>
    `;
  }

  generateDropdownAddToCartButton(product, availableVariants) {
    const translations = window.theme?.productCard?.translations;
    const showInventory = window.theme?.productCard?.formShowInventory;

    const dropdownItems = availableVariants
      .map((variant) => this.generateVariantDropdownItem(variant, showInventory, translations))
      .join("");

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
                data-product-url="${product.url || `/products/${product.handle}`}">
            <input type="hidden" name="id" value="${availableVariants[0].id}">
            <div class="w-200 dropup">
              <button type="button"
                      class="btn-atc btn btn-md dropdown-toggle btn-primary w-200"
                      data-action="add-to-cart-dropdown"
                      data-bs-toggle="dropdown"
                      data-text-add-to-cart="${translations?.addToCart || "Add To Cart"}"
                      aria-expanded="false"
                      aria-haspopup="true"
                      aria-label="${translations?.addToCart || "Add To Cart"} - Select variant">
                ${translations?.addToCart || "Add To Cart"}
              </button>
              <ul class="dropdown-menu shadow w-200">
                ${dropdownItems}
              </ul>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  generateVariantDropdownItem(variant, showInventory, translations) {
    const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

    return `
      <li class="${variant.inventory_policy || ""}">
        <button class="dropdown-item"
                onclick="onClickAtcFormVariant(this, event)"
                data-variant-id="${variant.id}"
                type="button"
                data-cnstrc-btn="add_to_cart"
                aria-label="Select ${ConstructorUtils.sanitizeHTML(variant.title)} variant"
                ${!variant.available ? "disabled aria-disabled='true'" : ""}>
          <div class="d-flex flex-column justify-content-start lh-sm">
            <span class="small">${ConstructorUtils.sanitizeHTML(variant.title)}</span>
            ${inventoryDisplay}
          </div>
        </button>
      </li>
    `;
  }

  getInventoryDisplay(variant, showInventory, translations) {
    if (!showInventory) {
      return "";
    }

    if (variant.available) {
      if (!variant.inventory_quantity || variant.inventory_quantity <= 0 || variant.inventory_quantity >= 10) {
        return `<small class="text-success d-block">${translations?.inStock || "In stock"}</small>`;
      } else {
        const stockText = translations?.stockCount || "Only [count] left in stock";
        return `<small class="text-danger d-block">${stockText.replace("[count]", variant.inventory_quantity)}</small>`;
      }
    } else {
      return `<small class="text-muted d-block">${translations?.soldOut || "Sold out"}</small>`;
    }
  }

  generateViewProductButtonMobile(product) {
    const translations = window.theme?.productCard?.translations;
    const productUrl = product.url || product.constructorData?.url || `/products/${product.handle}`;

    return `
      <div class="mobile-view d-block d-lg-none">
        <a href="${productUrl}" 
           class="btn-vp w-100 d-block"
           data-action="view-product-mobile"
           role="button">
          ${translations?.viewProduct || "View Product"}
        </a>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  onclick="addOrRemoveFromWishlist(this)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
  }

  generateSoldOutButtonMobile() {
    const translations = window.theme?.productCard?.translations;

    return `
      <div class="mobile-view d-block d-lg-none">
        <button class="btn-atc btn w-100 btn-primary" disabled>
          ${translations?.soldOut || "Sold Out"}
        </button>
      </div>
    `;
  }

  generateSimpleAddToCartButtonMobile(product, variant) {
    const translations = window.theme?.productCard?.translations;

    return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
              data-product-url="${product.url}">
          <input type="hidden" name="id" value="${variant.id}">
          <button class="btn-atc btn btn-md w-100 btn-primary"
                  type="submit"
                  name="add"
                  data-cnstrc-btn="add_to_cart"
                  data-text-add-to-cart="${translations?.addToCart || "Add to cart"}">
            ${translations?.addToCart || "Add to cart"}
          </button>
        </form>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  onclick="addOrRemoveFromWishlist(this)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
  }

  generateDropdownAddToCartButtonMobile(product, availableVariants) {
    const translations = window.theme?.productCard?.translations;
    const showInventory = window.theme?.productCard?.formShowInventory;

    const variantItems = availableVariants
      .map((variant) => this.generateVariantDropdownItemMobile(variant, showInventory, translations))
      .join("");

    return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
              data-product-url="${product.url}">
          <input type="hidden" name="id" value="${availableVariants[0].id}">
          <div class="w-100 dropup">
            <button type="button"
                    class="btn-atc w-100"
                    data-action="add-to-cart-mobile"
                    data-bs-toggle="dropdown"
                    data-text-add-to-cart="${translations?.addToCart || "Add to cart"}"
                    aria-expanded="false"
                    aria-haspopup="true"
                    aria-label="${translations?.addToCart || "Add to cart"} - Select variant">
              ${translations?.addToCart || "Add to cart"}
            </button>
            <ul class="dropdown-menu shadow w-100">
              ${variantItems}
            </ul>
          </div>
        </form>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  onclick="addOrRemoveFromWishlist(this)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
  }

  generateVariantDropdownItemMobile(variant, showInventory, translations) {
    const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

    return `
      <li class="${variant.inventory_policy || ""}">
        <button class="dropdown-item"
                onclick="onClickAtcFormVariant(this, event)"
                data-variant-id="${variant.id}"
                type="button"
                data-cnstrc-btn="add_to_cart"
                aria-label="Select ${ConstructorUtils.sanitizeHTML(variant.title)} variant"
                ${!variant.available ? "disabled aria-disabled='true'" : ""}>
          <div class="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center lh-sm">
            <span class="small">${ConstructorUtils.sanitizeHTML(variant.title)}</span>
            ${inventoryDisplay}
          </div>
        </button>
      </li>
    `;
  }

  generateViewProductButtonMobile(product) {
    const translations = window.theme?.productCard?.translations;
    const productUrl = product.url || product.constructorData?.url || `/products/${product.handle}`;

    return `
      <div class="mobile-view d-block d-lg-none">
        <a href="${productUrl}" 
           class="btn-vp w-100 d-block"
           data-action="view-product-mobile"
           role="button">
          ${translations?.viewProduct || "View Product"}
        </a>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  onclick="addOrRemoveFromWishlist(this)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
  }

  generateSoldOutButtonMobile() {
    const translations = window.theme?.productCard?.translations;

    return `
      <div class="mobile-view d-block d-lg-none">
        <button class="btn-atc btn w-100 btn-primary" disabled>
          ${translations?.soldOut || "Sold Out"}
        </button>
      </div>
    `;
  }

  generateSimpleAddToCartButtonMobile(product, variant) {
    const translations = window.theme?.productCard?.translations;

    return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
              data-product-url="${product.url}">
          <input type="hidden" name="id" value="${variant.id}">
          <button class="btn-atc btn btn-md w-100 btn-primary"
                  type="submit"
                  name="add"
                  data-cnstrc-btn="add_to_cart"
                  data-text-add-to-cart="${translations?.addToCart || "Add to cart"}">
            ${translations?.addToCart || "Add to cart"}
          </button>
        </form>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  onclick="addOrRemoveFromWishlist(this)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
  }

  generateDropdownAddToCartButtonMobile(product, availableVariants) {
    const translations = window.theme?.productCard?.translations;
    const showInventory = window.theme?.productCard?.formShowInventory;

    const variantItems = availableVariants
      .map((variant) => this.generateVariantDropdownItemMobile(variant, showInventory, translations))
      .join("");

    return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
              data-product-url="${product.url}">
          <input type="hidden" name="id" value="${availableVariants[0].id}">
          <div class="w-100 dropup">
            <button type="button"
                    class="btn-atc w-100"
                    data-action="add-to-cart-mobile"
                    data-bs-toggle="dropdown"
                    data-text-add-to-cart="${translations?.addToCart || "Add to cart"}"
                    aria-expanded="false"
                    aria-haspopup="true"
                    aria-label="${translations?.addToCart || "Add to cart"} - Select variant">
              ${translations?.addToCart || "Add to cart"}
            </button>
            <ul class="dropdown-menu shadow w-100">
              ${variantItems}
            </ul>
          </div>
        </form>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  onclick="addOrRemoveFromWishlist(this)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
  }
}

window.onChangeConstructorProductSwatches = (btn, event) => {
  event.preventDefault();
  event.stopPropagation();

  const productItem = btn.closest(".product-item");
  if (!productItem) {
    return;
  }

  const allSwatchesInProduct = productItem.querySelectorAll(".color-swatches .btn");
  allSwatchesInProduct.forEach((swatch) => swatch.classList.remove("selected"));

  btn.classList.add("selected");

  const productImg = productItem.querySelector(".product-item-img");
  if (productImg && btn.dataset.img) {
    productImg.src = btn.dataset.img;

    if (btn.dataset.imgWidth) productImg.width = btn.dataset.imgWidth;
    if (btn.dataset.imgHeight) productImg.height = btn.dataset.imgHeight;
  }
};

window.showAllConstructorSwatchesInProduct = (btn, event) => {
  event.preventDefault();
  event.stopPropagation();

  const swatchesList = btn.closest(".color-swatches");
  if (!swatchesList) {
    return;
  }

  swatchesList.querySelectorAll("li[hidden]").forEach((li) => {
    li.removeAttribute("hidden");
  });

  btn.remove();
};

window.onConstructorLoadMore = (btn, event) => {
  event.preventDefault();
  event.stopPropagation();

  if (window.constructorBrowse && window.constructorBrowse.isInitialized) {
    const nextPage = parseInt(btn.dataset.nextPage);
    const collectionHandle = btn.dataset.collectionHandle;
    const perPage = parseInt(btn.dataset.perPage);

    window.constructorBrowse.loadMoreProducts(collectionHandle, nextPage, perPage);
  }
};

function initializeConstructorBrowse() {
  const conditions = {
    isCollectionPage: window.cnstrc?.isCollectionPage,
    browseEnabled: window.cnstrc?.browseEnabled,
    collectionElement: !!document.getElementById("collection"),
    cnstrcBrowseElement: !!document.querySelector("[data-cnstrc-browse]"),
  };

  const shouldInitialize =
    conditions.isCollectionPage || conditions.browseEnabled || conditions.collectionElement || conditions.cnstrcBrowseElement;

  if (shouldInitialize) {
    window.constructorBrowse = ConstructorBrowse.getInstance();
  }
}

if (!window.constructorBrowseInitialized) {
  window.constructorBrowseInitialized = true;
  const boot = () => {
    initializeConstructorBrowse();
  };

  document.addEventListener("constructor:client:ready", boot, { once: true });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
}
